/* Lilla Guldfågeln: the product page (buy form, variants, 3D stand, original photos, facts, engraving, related pieces).
   Port of the prototype's productView() glue, the engraving preview (engraveBlock / bindEngrave), the original-photo
   viewer (origStrip / openPhoto / pz*) and the facts table. Plain ES2019, no build step, loaded deferred by sections/lg-product.liquid.

   Contracts it relies on (see shopify/BUILD-BRIEF.md):
   - window.LG from assets/lg.js: LG.products, LG.assets {specs, facts, three}, LG.cart.add(handleOrVariantId, qty, properties),
     LG.toast(text), LG.openPanel(el). Panels carry data-panel; [data-close] buttons and Esc close them; the cart is #cart.
     Product data: the #lg-data island, extra script[data-lg-data] islands ({products}) and each card's data-lg entry.
   - window.LG3D from assets/lg-3d.js: LG3D.viewer(canvas, handle, {onReady, onClick, onPhoto, onFail}) (Promise<Viewer|null>),
     LG3D.cutout(handle) (Promise of the trimmed cut-out photo), LG3D.hydrate(root).
   Everything degrades: without lg.js the buy form posts to /cart/add, without WebGL the stand shows the real photo. */
(function () {
  'use strict';
  if (window.LGProduct) return;   // several sections include this file: run it once
  const LGP = window.LGProduct = {};

  const $ = (s, el = document) => el.querySelector(s);
  const $$ = (s, el = document) => Array.from(el.querySelectorAll(s));
  const esc = s => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const FINE = matchMedia('(pointer: fine)').matches;
  const THREE_URL = 'https://cdnjs.cloudflare.com/ajax/libs/three.js/0.147.0/three.min.js';
  // the theme's own assets sit next to this file (used only when the layout has not loaded them already)
  const SELF = (document.currentScript && document.currentScript.src) || '';
  const assetUrl = name => (SELF ? SELF.replace(/lg-product\.js(\?.*)?$/, name) : '');
  const site = () => window.LG || null;
  const toast = t => { const L = site(); if (L && typeof L.toast === 'function') L.toast(t); };

  /* ---------- boot: after every deferred script (lg.js builds window.LG, lg-3d.js builds window.LG3D) ---------- */
  function whenBooted(fn) {
    let done = false;
    const go = () => { if (!done) { done = true; fn(); } };
    if (document.readyState === 'complete') go();
    else { document.addEventListener('DOMContentLoaded', () => setTimeout(go, 0)); window.addEventListener('load', go); }
  }

  /* ---------- product data ----------
     The site shell reads the page's products when it starts. Cards that arrive later (the recommendations) bring their
     own entries (data-lg on the card, or a script[data-lg-data] island): they are added to LG.products here, for handles
     LG does not know yet, before the 3D engine looks for them. */
  function mergeProducts(root) {
    const L = site(); if (!L) return false;
    const P = L.products || (L.products = {});
    const put = (h, x) => { if (h && x && typeof x === 'object' && !P[h]) P[h] = x; };
    $$('script[data-lg-data]', root).forEach(s => {
      try { const d = JSON.parse(s.textContent); Object.keys(d.products || {}).forEach(h => put(h, d.products[h])); } catch (e) { /* skip */ }
    });
    $$('[data-lg]', root).forEach(el => { try { const x = JSON.parse(el.dataset.lg); put(x.h, x); } catch (e) { /* skip */ } });
    return true;
  }
  const hydrate = root => { const E = window.LG3D; if (E && typeof E.hydrate === 'function') { try { E.hydrate(root); } catch (e) { /* the photo stays */ } } };

  // the 3D engine: the layout does not include it. The site shell's loader (LG.engine) adds lg-3d.js once, reusing a
  // tag that lg.js may already have added for the cards, and the engine then loads three.js r147 itself. Without
  // lg.js, three.js and lg-3d.js (next to this file) are loaded here.
  let engine = null;
  function loadScript(src) {
    return new Promise((res, rej) => {
      const s = document.createElement('script'); s.src = src; s.async = true;
      s.onload = () => res(); s.onerror = () => rej(new Error('load ' + src));
      document.head.appendChild(s);
    });
  }
  function ensure3D() {
    if (engine) return engine;
    engine = Promise.resolve().then(() => {
      if (window.LG3D) return window.LG3D;
      const L = site() || {}, three = (L.assets && L.assets.three) || THREE_URL;
      if (typeof L.engine === 'function') return L.engine();
      return (window.THREE ? Promise.resolve() : loadScript(three))
        .then(() => window.LG3D || loadScript(assetUrl('lg-3d.js')))
        .then(() => window.LG3D || Promise.reject(new Error('no 3D engine')));
    });
    return engine;
  }

  /* ---------- cart ---------- */
  function cartAdd(id, qty, props) {
    const L = site();
    if (!L || !L.cart || typeof L.cart.add !== 'function') return Promise.reject(new Error('no cart'));
    try { return Promise.resolve(props ? L.cart.add(id, qty, props) : L.cart.add(id, qty)); } catch (e) { return Promise.reject(e); }
  }
  const canAdd = () => { const L = site(); return !!(L && L.cart && typeof L.cart.add === 'function'); };
  function openCart() {
    const L = site(); if (!L) return;
    if (L.cart && typeof L.cart.open === 'function') { L.cart.open(); return; }
    const c = document.getElementById('cart');
    if (c && c.hidden && typeof L.openPanel === 'function') L.openPanel(c);
  }
  // Shopify explains a refused add (sold out, not enough in stock): lg.js passes its description on as the error's
  // message, with the HTTP status; anything else (no connection) gets a plain Swedish line
  const failText = err => (err && ((err.status && err.message) || err.description)) || 'Det gick inte att lägga i varukorgen. Försök igen eller ring oss.';
  function busy(btn, on) { if (!btn) return; btn.disabled = on; if (on) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy'); }

  /* ---------- panels: the site shell's, or a minimal one of our own when lg.js is missing ---------- */
  function openPanel(el) {
    const L = site();
    if (L && typeof L.openPanel === 'function') L.openPanel(el);
    else { el.hidden = false; document.body.style.overflow = 'hidden'; }
  }
  function closePanel(el) {
    if (el.hidden) return;
    const L = site(), x = $('[data-close]', el);
    if (L && typeof L.openPanel === 'function' && x) x.click();   // the shell closes it (scrim, inert, focus back)
    if (!el.hidden) { el.hidden = true; document.body.style.overflow = ''; }
  }
  const ownPanels = () => { const L = site(); return !(L && typeof L.openPanel === 'function'); };

  /* ---------- original photos: the full-screen viewer ----------
     Zoom with wheel, click, pinch, buttons or keys; drag to pan; swipe or arrows between photos. */
  function photoViewer(sec) {
    let list = [];
    try { list = JSON.parse(($('script[data-pz-photos]', sec) || {}).textContent || '[]'); } catch (e) { list = []; }
    const lb = $('#lightbox', sec) || document.getElementById('lightbox');
    const api = { list, open() {} };
    if (!lb || !list.length) return api;
    document.body.appendChild(lb);   // out of <main>, so it stays usable while the page behind it is inert
    const st = $('#pzStage', lb), im = $('#lbImg', lb), load = $('#pzLoad', lb), cap = $('#lbCap', lb).textContent;
    const pct = $('#pzPct', lb), bIn = $('#pzIn', lb), bOut = $('#pzOut', lb), bFit = $('#pzFit', lb), prev = $('#pzPrev', lb), next = $('#pzNext', lb);
    const thumbs = $('#pzThumbs', lb);
    const PZ = { i: 0, s: 1, x: 0, y: 0, fit: { w: 1, h: 1, W: 1, H: 1 }, nat: { w: 1, h: 1 }, max: 4 };

    function open(i) {
      $('#pzHelp', lb).textContent = FINE ? 'Scrolla eller klicka för att zooma · dra för att flytta · Esc stänger' : 'Nyp eller tryck för att zooma · dra för att flytta';
      thumbs.innerHTML = list.length > 1 ? list.map((x, k) => `<button type="button" data-pz="${k}" aria-label="Foto ${k + 1}"><img src="${esc(x.lo || x.hi)}" alt="" loading="lazy" width="56" height="56"></button>`).join('') : '';
      openPanel(lb); show(i || 0);
      const c = $('[data-close]', lb); if (c) c.focus();
    }
    function show(i) {
      const n = list.length; PZ.i = ((i % n) + n) % n; const ph = list[PZ.i];
      $('#pzCount', lb).textContent = n > 1 ? `${PZ.i + 1} av ${n}` : '';
      prev.hidden = next.hidden = n < 2;
      $$('button', thumbs).forEach((b, k) => b.setAttribute('aria-current', k === PZ.i));
      im.alt = `${cap}, originalfoto${n > 1 ? ' ' + (PZ.i + 1) : ''}`;
      // the small photo shows at once; the full-size one takes over when it has loaded (same framing, zoom kept)
      im.removeAttribute('src'); PZ.nat = { w: ph.w || 1000, h: ph.h || 1000 }; layout(true);
      if (ph.lo) im.src = ph.lo;
      const hi = new Image(); load.hidden = false;
      hi.onload = () => { if (list[PZ.i] !== ph) return; im.src = ph.hi; load.hidden = true; PZ.nat = { w: hi.naturalWidth, h: hi.naturalHeight }; layout(!ph.lo); };
      hi.onerror = () => { load.hidden = true; };
      hi.src = ph.hi;
    }
    function layout(reset) {
      const W = st.clientWidth || 1, H = st.clientHeight || 1, a = PZ.nat.w / PZ.nat.h;
      let w = W, h = W / a; if (h > H) { h = H; w = H * a; }
      const old = PZ.fit; PZ.fit = { w, h, W, H };
      PZ.max = Math.max(4, Math.min(8, PZ.nat.w / w * 1.5));   // at least 4×; up to 1.5× beyond the photo's own pixels
      im.style.width = w + 'px'; im.style.height = h + 'px';
      if (reset) { PZ.s = 1; PZ.x = (W - w) / 2; PZ.y = (H - h) / 2; } else if (old.w !== w) { PZ.x = PZ.x * w / old.w; PZ.y = PZ.y * h / old.h; }
      apply();
    }
    function apply(anim) {
      const { w, h, W, H } = PZ.fit, sw = w * PZ.s, sh = h * PZ.s;
      PZ.x = sw <= W ? (W - sw) / 2 : Math.min(0, Math.max(W - sw, PZ.x));   // never pan past the photo's edges
      PZ.y = sh <= H ? (H - sh) / 2 : Math.min(0, Math.max(H - sh, PZ.y));
      im.classList.toggle('anim', !!anim); im.style.transform = `translate(${PZ.x}px,${PZ.y}px) scale(${PZ.s})`;
      pct.textContent = Math.round(PZ.s * 100) + ' %'; st.classList.toggle('zoomed', PZ.s > 1.01);
      bOut.disabled = PZ.s <= 1.001; bIn.disabled = PZ.s >= PZ.max - 0.001;
    }
    function zoom(s, cx, cy, anim) {   // keeps the point (cx,cy) still
      s = Math.max(1, Math.min(PZ.max, s)); const k = s / PZ.s;
      PZ.x = cx - (cx - PZ.x) * k; PZ.y = cy - (cy - PZ.y) * k; PZ.s = s; apply(anim);
    }

    // pointer: drag to pan, pinch to zoom, click or tap to zoom in (or back out); outside the photo it closes
    const pts = new Map(); let g = null, moved = false;
    const rel = e => { const r = st.getBoundingClientRect(); return { x: e.clientX - r.left, y: e.clientY - r.top }; };
    const onImg = q => q.x >= PZ.x && q.x <= PZ.x + PZ.fit.w * PZ.s && q.y >= PZ.y && q.y <= PZ.y + PZ.fit.h * PZ.s;
    const regrip = () => {
      const v = Array.from(pts.values());
      if (v.length === 1) g = { x: v[0].x, y: v[0].y, px: PZ.x, py: PZ.y };
      else if (v.length === 2) { const [a, b] = v; g = { d: Math.hypot(a.x - b.x, a.y - b.y) || 1, s: PZ.s, cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2, px: PZ.x, py: PZ.y }; }
      else g = null;
    };
    st.addEventListener('wheel', e => { e.preventDefault(); const q = rel(e); zoom(PZ.s * Math.exp(-e.deltaY * 0.0022), q.x, q.y); }, { passive: false });
    st.addEventListener('pointerdown', e => {
      if (e.target.closest('button')) return;
      try { st.setPointerCapture(e.pointerId); } catch (_) { /* fine */ }
      pts.set(e.pointerId, rel(e)); if (pts.size === 1) moved = false; regrip();
    });
    st.addEventListener('pointermove', e => {
      if (!pts.has(e.pointerId) || !g) return; pts.set(e.pointerId, rel(e)); const v = Array.from(pts.values());
      if (v.length === 2 && g.d) {   // pinch
        const [a, b] = v, cx = (a.x + b.x) / 2, cy = (a.y + b.y) / 2, s = Math.max(1, Math.min(PZ.max, g.s * Math.hypot(a.x - b.x, a.y - b.y) / g.d)), k = s / g.s;
        moved = true; PZ.s = s; PZ.x = g.cx - (g.cx - g.px) * k + (cx - g.cx); PZ.y = g.cy - (g.cy - g.py) * k + (cy - g.cy); apply(); return;
      }
      if (v.length === 1 && !g.d) {
        const dx = v[0].x - g.x, dy = v[0].y - g.y; if (Math.abs(dx) + Math.abs(dy) > 5) moved = true;
        if (moved && PZ.s > 1.01) { st.classList.add('dragging'); PZ.x = g.px + dx; PZ.y = g.py + dy; apply(); }
      }
    });
    const up = e => {
      if (!pts.has(e.pointerId)) return; const q = rel(e), was = g; pts.delete(e.pointerId); st.classList.remove('dragging');
      if (pts.size === 0) {
        if (!moved) {   // a click or tap: zoom in where pressed, or back to the whole photo; outside the photo it closes
          if (PZ.s > 1.01) zoom(1, q.x, q.y, true); else if (onImg(q)) zoom(2.5, q.x, q.y, true); else closePanel(lb);
        } else if (PZ.s <= 1.01 && was && !was.d && list.length > 1) { const dx = q.x - was.x; if (Math.abs(dx) > 60) show(PZ.i + (dx < 0 ? 1 : -1)); }   // swipe between photos
        g = null;
      } else regrip();
    };
    st.addEventListener('pointerup', up); st.addEventListener('pointercancel', up);
    const mid = () => [PZ.fit.W / 2, PZ.fit.H / 2];
    bIn.addEventListener('click', () => zoom(PZ.s * 1.6, ...mid(), true));
    bOut.addEventListener('click', () => zoom(PZ.s / 1.6, ...mid(), true));
    bFit.addEventListener('click', () => zoom(1, ...mid(), true));
    prev.addEventListener('click', () => show(PZ.i - 1)); next.addEventListener('click', () => show(PZ.i + 1));
    thumbs.addEventListener('click', e => { const b = e.target.closest('[data-pz]'); if (b) show(+b.dataset.pz); });
    window.addEventListener('resize', () => { if (!lb.hidden) layout(false); });
    document.addEventListener('keydown', e => {
      if (lb.hidden) return; const k = e.key, z = PZ.s > 1.01;
      if (k === 'Escape') { if (ownPanels()) closePanel(lb); }   // the site shell closes its panels on Esc itself
      else if (k === '+' || k === '=') bIn.click(); else if (k === '-' || k === '_') bOut.click(); else if (k === '0') bFit.click();
      else if (k === 'ArrowLeft' || k === 'ArrowRight') { e.preventDefault(); const d = k === 'ArrowRight' ? 1 : -1; if (z) { PZ.x -= d * 90; apply(true); } else if (list.length > 1) show(PZ.i + d); }
      else if ((k === 'ArrowUp' || k === 'ArrowDown') && z) { e.preventDefault(); PZ.y -= (k === 'ArrowDown' ? 1 : -1) * 90; apply(true); }
    });
    lb.addEventListener('click', e => { if (ownPanels() && e.target.closest('[data-close]')) closePanel(lb); });

    $$('.pv-thumb', sec).forEach(b => b.addEventListener('click', () => open(+b.dataset.photo)));
    api.open = open;
    return api;
  }

  /* ---------- the 3D stand ---------- */
  function openable(el, fn) {
    if (el.dataset.openable) return; el.dataset.openable = '1';
    if (!el.hasAttribute('tabindex')) el.tabIndex = 0;
    if (!el.hasAttribute('role')) el.setAttribute('role', 'button');
    el.addEventListener('click', fn);
    el.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); } });
  }
  function initViewer(sec, photos) {
    const pv = $('#pv', sec); if (!pv) return;
    // the viewer opens on the chosen variant's own photo (data-photo, kept up to date by the variant picker)
    const h = sec.dataset.h, cv = $('#pvc', pv), open = () => photos.open(+(pv.dataset.photo || 0));
    // a photo product (or one the engine cannot stage) shows its real photo; clicking it opens it full size
    const asPhoto = () => {
      if (pv.classList.contains('scene')) return;
      if (cv) cv.remove(); const hint = $('.vhint', pv); if (hint) hint.remove();
      pv.classList.remove('ready', 'nostudio'); pv.classList.add('scene');
      const im = $('img.cut', pv), first = photos.list[+(pv.dataset.photo || 0)] || photos.list[0];
      if (im && first) { im.removeAttribute('srcset'); im.removeAttribute('sizes'); im.src = first.hi; }
      openable(pv, open);
      const t = $('.pv-orig-txt p', sec); if (t) t.textContent = 'Klicka på bilden för att förstora den och zooma in på detaljerna.';
    };
    if (pv.classList.contains('scene')) { openable(pv, open); return; }
    if (!cv) { asPhoto(); return; }
    ensure3D().then(E => {
      if (typeof E.viewer !== 'function') throw new Error('no viewer');
      let ready = false, failed = false, v = null;
      // the engine tells through onPhoto when a lifestyle photo cannot stand on a holder, and through onFail when 3D is unavailable
      const fail = () => { if (failed || ready) return; failed = true; try { if (v && v.dispose) v.dispose(); } catch (e) { /* gone */ } asPhoto(); };
      Promise.resolve(E.viewer(cv, h, { onReady: () => { ready = true; pv.classList.add('ready'); }, onClick: open, onPhoto: fail, onFail: fail }))
        .then(r => {
          if (!r) { fail(); return; }
          LGP.viewer = v = r;
          // the engine is here; a stand that still never comes (nothing it could build) gives way to the photo
          setTimeout(() => { if (!ready) fail(); }, 20000);
        }, fail);
    }).catch(asPhoto);
  }

  /* ---------- the shop's listed facts (assets/lg-facts.json) ---------- */
  let factsJob = null;
  function facts() {
    if (!factsJob) {
      const L = site() || {}, url = (L.assets && L.assets.facts) || assetUrl('lg-facts.json');
      factsJob = url ? fetch(url).then(r => (r.ok ? r.json() : {})).catch(() => ({})) : Promise.resolve({});
    }
    return factsJob;
  }
  const nice = v => v && String(v).replace(/(\d)\s*(g|cm|mm)\b/g, '$1 $2').replace(/(\d)\.(\d)/g, '$1,$2').replace(/(\d)\s*x\s*(\d)/gi, '$1 × $2')
    .replace(/\b(\d{1,2})\s*k\b/gi, '$1 K').replace(/\bSilver\b/, 'silver').replace(/\bGuld\b/, 'guld');
  function initFacts(sec) {
    const h = sec.dataset.h, m = sec.dataset.m || null, cat = sec.dataset.cat || '';
    facts().then(all => {
      const F = all && all[h]; if (!F) return;
      // the shop's own listed facts first; metal and purity only when the listing doesn't say
      const specs = [
        ['Material', nice(F.material) || (m === 'guld' ? 'Guld, 18 K (750)' : m === 'silver' ? '925 sterlingsilver' : null)],
        ['Vikt', nice(F.vikt)], ['Längd', nice(F.langd)], ['Mått', nice(F.matt)], ['Höjd', nice(F.hojd)], ['Storlek', F.storlek],
        ['Stenar', F.stenar && String(F.stenar).replace(/\s*stenar$/i, '').replace(/^kubisk zirkonia$/i, 'Kubisk zirkonia (KZ)')],
        ['Kategori', cat]
      ].filter(x => x[1]);
      const box = $('[data-specs]', sec);
      if (box) box.innerHTML = specs.length > 1 ? `<dl class="specs">${specs.map(([k, v]) => `<dt>${k}</dt><dd>${esc(v)}</dd>`).join('')}</dl>` : '<div style="height:26px"></div>';
      // a piece's hallmark: its metal, or 925 when a gift's own listing says sterling silver
      const hm = $('[data-hallmark]', sec);
      if (!m && hm && /925/.test(F.material || '')) hm.outerHTML = `<div style="display:flex;gap:10px;align-items:center" data-hallmark><span class="stamp ag">925</span><span class="eyebrow">${esc(cat)}</span></div>`;
    });
  }

  /* ---------- engraving preview ----------
     The shop's photo has a white background: the 3D engine cuts the piece out and trims it (LG3D.cutout, the port of
     tools/cutout.ps1), so the text lands where the table in lg-product.liquid says (fractions of the trimmed photo). */
  const engNote = lines => lines.map(s => s.trim()).filter(Boolean).join(' / ');
  function initEngrave(sec, idOf, form) {
    const box = $('[data-eng]', sec); if (!box) return;
    const e = { w: parseFloat(box.dataset.w) || 0.5 };
    const txt = $('#engTxt', box), sp = txt.querySelectorAll('span'), img = $('#engImg', box), stage = $('#engStage', box), prev = $('.eng-prev', box);
    const ins = ['#eng1', '#eng2', '#eng3'].map(s => $(s, box)).filter(Boolean);
    // never upscale the cut-out photo past 1.5× its own pixels, so the tag's edges stay clean
    const setW = () => { const a = (img.naturalWidth / img.naturalHeight) || 1, w = prev.clientWidth * 0.88 || 1, cap = img.naturalWidth * 1.5 / w; stage.style.width = (Math.min(1, a, cap) * 100) + '%'; };
    const reveal = () => { txt.style.visibility = ''; setW(); };
    let started = false;
    const start = () => {
      if (started) return; started = true;
      // cut from the photo at 1200 px: the piece fills only part of the shop's photo, and the text must stay readable
      ensure3D().then(E => E.cutout(sec.dataset.h, 1200)).then(url => {
        img.onload = reveal; img.removeAttribute('srcset'); img.removeAttribute('sizes'); img.removeAttribute('width'); img.removeAttribute('height');
        img.loading = 'eager'; img.src = url;
      }).catch(() => { if (img.complete) reveal(); else img.addEventListener('load', reveal); });   // without the cut-out the text still shows, on the full photo
    };
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(es => { if (es.some(x => x.isIntersecting)) { io.disconnect(); start(); } }, { rootMargin: '600px' });
      io.observe(box);
    } else start();
    window.addEventListener('resize', () => { if (img.naturalWidth && txt.style.visibility !== 'hidden') setW(); });

    const draw = () => {
      const caps = txt.classList.contains('caps');
      // an empty field previews its example text, faded, so it reads as an example
      sp.forEach((s, i) => { const v = ins[i] ? (ins[i].value || ins[i].placeholder || '') : ''; s.textContent = v; s.hidden = !v.trim(); s.style.opacity = ins[i] && !ins[i].value ? 0.5 : 1; });
      const n0 = Math.max(1, sp[0].textContent.length), f0 = e.w * 100 / (caps ? 8.5 : 4.2) * Math.min(1, (caps ? 11 : 8) / n0);
      sp[0].style.setProperty('--fs', f0.toFixed(2));
      // the lines under the name never outgrow it
      for (let i = 1; i < 3; i++) { const f = e.w * 100 / 11 * Math.min(1, 15 / Math.max(1, sp[i].textContent.length)); sp[i].style.setProperty('--fs', Math.min(f, f0 * (caps ? 0.8 : 0.62)).toFixed(2)); }
    };
    ins.forEach(i => i.addEventListener('input', draw));
    const segs = $$('[data-engs]', box);
    segs.forEach(b => b.addEventListener('click', () => {
      segs.forEach(x => x.setAttribute('aria-pressed', x === b));
      txt.classList.toggle('caps', b.dataset.engs === 'caps'); txt.classList.toggle('script', b.dataset.engs !== 'caps'); draw();
    }));

    // the engraving goes into the cart as line item properties: Gravyr (the lines) and Gravyrstil (Skrivstil or Versaler)
    const add = $('#engAdd', box);
    add.addEventListener('click', () => {
      if (!ins.some(i => i.value.trim())) { ins[0].focus(); toast('Skriv texten som ska graveras'); return; }
      const props = { Gravyr: engNote(ins.map(i => i.value)), Gravyrstil: txt.classList.contains('caps') ? 'Versaler' : 'Skrivstil' };
      if (!canAdd()) {   // no site shell: post the form with the properties
        Object.keys(props).forEach(k => { const f = document.createElement('input'); f.type = 'hidden'; f.name = `properties[${k}]`; f.value = props[k]; form.appendChild(f); });
        const qv = $('[name="quantity"]', form); if (qv) qv.value = 1;
        form.submit(); return;
      }
      busy(add, true);
      cartAdd(idOf(), 1, props).then(openCart, err => toast(failText(err))).then(() => busy(add, false));
    });
    draw();
  }

  /* ---------- the product page ---------- */
  function initProduct(sec) {
    const form = $('form[action*="/cart/add"]', sec);
    const idEl = form && $('[name="id"]', form);
    const idOf = () => +(idEl ? idEl.value : 0);
    const addBtn = $('#addBtn', sec);

    // quantity: 1 to 9, as in the prototype
    let q = 1; const qn = $('#qn', sec), qv = $('#qv', sec);
    const setQ = v => { q = Math.max(1, Math.min(9, v)); if (qn) qn.textContent = q; if (qv) qv.value = q; };
    const qm = $('#qm', sec), qp = $('#qp', sec);
    if (qm) qm.addEventListener('click', () => setQ(q - 1));
    if (qp) qp.addEventListener('click', () => setQ(q + 1));

    // variants (only products that have them): price, availability and the address follow the choice
    const sel = $('[data-variant]', sec);
    if (sel) sel.addEventListener('change', () => {
      const o = sel.selectedOptions[0]; if (!o) return;
      const avail = o.dataset.avail === 'true', price = $('span[data-price]', sec), cmp = $('s[data-compare]', sec), eng = $('#engAdd [data-eng-price]', sec), engAdd = $('#engAdd', sec);
      if (price) price.textContent = o.dataset.price;
      // delivery lines by price band (see the band comment in lg-product.liquid)
      const c = +o.dataset.cents; if (c) { const band = c > 2000000 ? 'pickup' : c >= 500000 ? 'rek' : 'std'; $$('[data-band]', sec).forEach(e => { e.hidden = !e.dataset.band.split(' ').includes(band) }) }
      if (cmp) { cmp.textContent = o.dataset.compare || ''; cmp.hidden = !o.dataset.compare; }
      if (eng) eng.textContent = o.dataset.engPrice;
      if (addBtn) { addBtn.disabled = !avail; addBtn.textContent = avail ? 'Lägg i varukorgen' : 'Slutsåld'; }
      if (engAdd) engAdd.disabled = !avail;
      // a variant with its own photo: the photo hero (watches) shows it, and the photo viewer opens on it
      const pv = $('#pv', sec), im = pv && $('img.cut', pv);
      if (pv && o.dataset.photo) pv.dataset.photo = o.dataset.photo;
      if (im && o.dataset.img && pv.classList.contains('scene')) { im.removeAttribute('srcset'); im.removeAttribute('sizes'); im.src = o.dataset.img; }
      try { const u = new URL(location.href); u.searchParams.set('variant', o.value); history.replaceState(history.state, '', u.toString()); } catch (e) { /* fine */ }
    });

    // buy: through the site's cart (AJAX) and open the cart; without it the form posts to /cart/add.
    // An app may own the form too (Kaching Bundles): when it has already handled the submit we stay out of it, the
    // quantity is the form's own field (a "buy 2 / buy 3" bundle sets it; the stepper keeps it in step), and
    // properties[...] fields that an app puts into the form travel along.
    if (form) form.addEventListener('submit', e => {
      if (e.defaultPrevented || !canAdd() || !idOf()) return;
      e.preventDefault();
      const props = {};
      Array.from(form.elements).forEach(f => { const m = /^properties\[(.+)\]$/.exec(f.name || ''); if (m && f.value && !((f.type === 'checkbox' || f.type === 'radio') && !f.checked)) props[m[1]] = f.value; });
      const fq = parseInt(new FormData(form).get('quantity'), 10), qty = fq > 0 ? fq : q;
      busy(addBtn, true);
      cartAdd(idOf(), qty, Object.keys(props).length ? props : null).then(openCart, err => toast(failText(err))).then(() => busy(addBtn, false));
    });

    if (!FINE) $$('[data-touch]', sec).forEach(el => { el.textContent = el.dataset.touch; });
    const photos = photoViewer(sec);
    initViewer(sec, photos);
    initFacts(sec);
    if (form) initEngrave(sec, idOf, form);
  }

  /* ---------- related pieces: Shopify's recommendations when the category has nothing else ---------- */
  function initRelated() {
    $$('[data-lg-related][data-recs]').forEach(box => {
      if (!box.hasAttribute('data-empty')) return;
      fetch(box.dataset.recs).then(r => (r.ok ? r.text() : '')).then(html => {
        if (!html) return;
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-lg-related]');
        if (!fresh || fresh.hasAttribute('data-empty')) return;
        box.innerHTML = fresh.innerHTML; box.removeAttribute('data-empty');
        mergeProducts(box);
        if (window.LG3D) hydrate(box); else ensure3D().then(() => hydrate(box), () => {});
      }).catch(() => {});
    });
  }

  whenBooted(() => {
    mergeProducts(document);
    const sec = $('[data-lg-product]');
    if (sec) {
      initProduct(sec);
      // the related pieces stage on the same engine as the stand
      ensure3D().then(() => $$('[data-lg-related]').forEach(hydrate), () => {});
    }
    initRelated();
  });

  LGP.mergeProducts = mergeProducts;   // for sections that add products later (e.g. the theme editor)
})();
